import { useRef, useState } from 'react'
import { Paperclip, Plus, Trash2 } from 'lucide-react'
import { centsFromInput, formatDate, formatMoney, inputFromCents } from '@mavatech/shared'
import { RECEIPT_CATEGORIES } from '@mavatech/shared'
import { Button, Dialog, Input, Select, Textarea } from '@/components/ui'
import { trpc } from '@/lib/trpc'
import { useAppStore } from '@/stores/app.store'

const iso = (d?: string | Date | null) => (d ? new Date(d).toISOString().slice(0, 10) : '')

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '')
    r.onerror = reject
    r.readAsDataURL(file)
  })
}

export function ReceiptsTab({ year }: { year: number }) {
  const utils = trpc.useUtils()
  const addToast = useAppStore((s) => s.addToast)
  const list = trpc.receipt.list.useQuery({ year })
  const stats = trpc.receipt.stats.useQuery({ year })
  const [editing, setEditing] = useState<null | Record<string, any>>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const refresh = () => {
    utils.receipt.list.invalidate()
    utils.receipt.stats.invalidate()
  }
  const onErr = (e: { message: string }) => addToast({ type: 'error', title: 'Fehler', description: e.message })
  const createM = trpc.receipt.create.useMutation({ onSuccess: () => { refresh(); setEditing(null); addToast({ type: 'success', title: 'Beleg gespeichert' }) }, onError: onErr })
  const updateM = trpc.receipt.update.useMutation({ onSuccess: () => { refresh(); setEditing(null); addToast({ type: 'success', title: 'Gespeichert' }) }, onError: onErr })
  const removeM = trpc.receipt.remove.useMutation({ onSuccess: refresh, onError: onErr })
  const upload = trpc.asset.upload.useMutation()

  const blank = () => ({
    date: iso(new Date()),
    paymentDate: '',
    supplierName: '',
    supplierVatId: '',
    invoiceNumberExternal: '',
    description: '',
    category: RECEIPT_CATEGORIES[0],
    gross: '0,00',
    vatRatePercent: '20',
    vatDeductible: true,
    reverseCharge: false,
    fileAssetId: null as string | null,
    notes: '',
  })

  const openNew = () => setEditing(blank())
  const openEdit = (r: any) =>
    setEditing({
      id: r.id,
      date: iso(r.date),
      paymentDate: iso(r.paymentDate),
      supplierName: r.supplierName ?? '',
      supplierVatId: r.supplierVatId ?? '',
      invoiceNumberExternal: r.invoiceNumberExternal ?? '',
      description: r.description ?? '',
      category: r.category ?? RECEIPT_CATEGORIES[0],
      gross: inputFromCents(r.grossCents),
      vatRatePercent: String(r.vatRatePercent ?? 20),
      vatDeductible: r.vatDeductible,
      reverseCharge: r.reverseCharge,
      fileAssetId: r.fileAssetId ?? null,
      notes: r.notes ?? '',
    })

  const save = () => {
    if (!editing) return
    const payload = {
      date: new Date(editing.date),
      paymentDate: editing.paymentDate ? new Date(editing.paymentDate) : null,
      supplierName: editing.supplierName || null,
      supplierVatId: editing.supplierVatId || null,
      invoiceNumberExternal: editing.invoiceNumberExternal || null,
      description: editing.description || null,
      category: editing.category || null,
      grossCents: centsFromInput(editing.gross),
      vatRatePercent: Number(editing.vatRatePercent),
      vatDeductible: !!editing.vatDeductible,
      reverseCharge: !!editing.reverseCharge,
      fileAssetId: editing.fileAssetId || null,
      notes: editing.notes || null,
    }
    if (editing.id) updateM.mutate({ id: editing.id, ...payload })
    else createM.mutate(payload)
  }

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !editing) return
    const dataBase64 = await fileToBase64(file)
    const res = await upload.mutateAsync({ filename: file.name, mime: file.type || 'application/pdf', dataBase64, kind: 'other' }).catch((err) => {
      onErr(err)
      return null
    })
    if (res) setEditing({ ...editing, fileAssetId: res.id })
    e.target.value = ''
  }

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div className="text-sm text-text-secondary">
          {stats.data ? (
            <>
              {stats.data.count} Belege · Netto {formatMoney(stats.data.netCents)} · Vorsteuer {formatMoney(stats.data.vorsteuerCents)}
            </>
          ) : (
            '…'
          )}
        </div>
        <Button variant="primary" onClick={openNew}>
          <Plus className="w-4 h-4" /> Beleg erfassen
        </Button>
      </div>

      {!list.data?.length ? (
        <p className="text-sm text-text-tertiary">Keine Belege in {year}.</p>
      ) : (
        <div className="overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th>Datum</th>
              <th>Lieferant</th>
              <th>Kategorie</th>
              <th className="text-right">Netto</th>
              <th className="text-right">VSt</th>
              <th className="text-right">Brutto</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.data.map((r) => (
              <tr key={r.id} className="cursor-pointer" onClick={() => openEdit(r)}>
                <td className="text-text-secondary">{formatDate(r.date as unknown as string)}</td>
                <td className="text-text-primary">
                  {r.supplierName ?? '—'}
                  {r.fileAssetId ? <Paperclip className="w-3 h-3 inline ml-1.5 text-text-tertiary" /> : null}
                </td>
                <td className="text-text-tertiary text-xs">{r.category ?? '—'}</td>
                <td className="text-right text-money">{formatMoney(r.netCents ?? r.grossCents)}</td>
                <td className="text-right text-money text-text-secondary">{r.vatDeductible ? formatMoney(r.vatCents ?? 0) : '—'}</td>
                <td className="text-right text-money text-text-primary">{formatMoney(r.grossCents)}</td>
                <td>
                  <button
                    className="text-text-tertiary hover:text-danger"
                    onClick={(e) => {
                      e.stopPropagation()
                      removeM.mutate({ id: r.id })
                    }}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)} title={editing?.id ? 'Beleg bearbeiten' : 'Beleg erfassen'} wide>
        {editing && (
          <div className="grid grid-cols-2 gap-4">
            <Input label="Belegdatum" type="date" value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} />
            <Input label="Zahlungsdatum" type="date" value={editing.paymentDate} onChange={(e) => setEditing({ ...editing, paymentDate: e.target.value })} />
            <Input label="Lieferant" value={editing.supplierName} onChange={(e) => setEditing({ ...editing, supplierName: e.target.value })} />
            <Input label="Lieferanten-UID" value={editing.supplierVatId} onChange={(e) => setEditing({ ...editing, supplierVatId: e.target.value })} />
            <Input label="Fremd-Belegnr." value={editing.invoiceNumberExternal} onChange={(e) => setEditing({ ...editing, invoiceNumberExternal: e.target.value })} />
            <Select label="Kategorie" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} options={RECEIPT_CATEGORIES.map((c) => ({ value: c, label: c }))} />
            <Input label="Brutto (€)" inputMode="decimal" value={editing.gross} onChange={(e) => setEditing({ ...editing, gross: e.target.value })} />
            <Select
              label="USt-Satz"
              value={editing.vatRatePercent}
              onChange={(e) => setEditing({ ...editing, vatRatePercent: e.target.value })}
              options={[
                { value: '20', label: '20 %' },
                { value: '13', label: '13 %' },
                { value: '10', label: '10 %' },
                { value: '0', label: '0 %' },
              ]}
            />
            <label className="flex items-center gap-2 text-sm text-text-secondary">
              <input type="checkbox" checked={editing.vatDeductible} onChange={(e) => setEditing({ ...editing, vatDeductible: e.target.checked })} />
              Vorsteuer abziehbar
            </label>
            <label className="flex items-center gap-2 text-sm text-text-secondary">
              <input type="checkbox" checked={editing.reverseCharge} onChange={(e) => setEditing({ ...editing, reverseCharge: e.target.checked })} />
              Reverse Charge (EU / § 19)
            </label>
            <div className="col-span-2">
              <Textarea label="Beschreibung" rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
            </div>
            <div className="col-span-2 flex items-center gap-3">
              <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={onFile} />
              <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()} loading={upload.isPending}>
                <Paperclip className="w-3.5 h-3.5" /> {editing.fileAssetId ? 'Scan ersetzen' : 'Scan anhängen'}
              </Button>
              {editing.fileAssetId && <span className="text-xs text-success">angehängt</span>}
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="ghost" onClick={() => setEditing(null)}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={save} loading={createM.isPending || updateM.isPending}>
            Speichern
          </Button>
        </div>
      </Dialog>
    </div>
  )
}
